import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaHeart, FaCalendarAlt, FaMapMarkerAlt, FaPlus, FaPaperPlane, FaTimes, FaQuoteLeft, FaTag } from 'react-icons/fa';
import './MemoriesPage.css';

const MemoriesPage = () => {
  const [memories, setMemories] = useState([]);
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newMemory, setNewMemory] = useState({
    title: '',
    date: '',
    location: '',
    description: '',
    mood: '',
    tags: ''
  });

  // 获取回忆列表
  const fetchMemories = async () => {
    try {
      const response = await fetch('/api/memories');
      const result = await response.json();
      if (result.success) {
        setMemories(result.data);
      } else {
        setMemories([]);
      }
    } catch (error) {
      console.error('获取回忆失败:', error);
      setMemories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMemories();
  }, []);

  const openMemoryDetail = (memory) => {
    setSelectedMemory(memory);
  };

  const closeMemoryDetail = () => {
    setSelectedMemory(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewMemory(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newMemory.title.trim() || !newMemory.date.trim() || !newMemory.location.trim() || !newMemory.description.trim()) {
      alert('请填写完整的回忆信息！');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/memories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: newMemory.title.trim(),
          date: newMemory.date.trim(),
          location: newMemory.location.trim(),
          description: newMemory.description.trim(),
          mood: newMemory.mood.trim() || '😊',
          tags: newMemory.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // 重新获取回忆列表
        await fetchMemories();
        setNewMemory({ title: '', date: '', location: '', description: '', mood: '', tags: '' });
        setShowAddForm(false);
        alert('回忆添加成功！');
      } else {
        alert(result.message || '提交失败，请重试！');
      }
    } catch (error) {
      console.error('提交回忆失败:', error);
      alert('网络错误，请检查连接后重试！');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelForm = () => {
    setNewMemory({ title: '', date: '', location: '', description: '', mood: '', tags: '' });
    setShowAddForm(false);
  };

  return (
    <motion.div
      className="memories-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
    >
      {/* 顶部区域 */}
      <div className="memories-top-section">
        <Link to="/" className="back-btn">
          <FaArrowLeft /> 返回首页
        </Link>
        
        <motion.div 
          className="memories-header"
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <h1 className="memories-title">
            <FaHeart className="title-icon" />
            点点滴滴
            <FaHeart className="title-icon" />
          </h1>
          <p className="memories-subtitle">记录我们一起走过的美好时光</p>
        </motion.div>
      </div>

      {/* 波浪分隔 */}
      <div className="memories-wave-divider">
        <svg viewBox="0 0 600 200" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <defs>
            <linearGradient id="memoriesWaveGradient" x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" style={{stopColor: 'rgba(255, 240, 245, 0.4)', stopOpacity: 1}} />
              <stop offset="30%" style={{stopColor: 'rgba(255, 228, 235, 0.5)', stopOpacity: 1}} />
              <stop offset="70%" style={{stopColor: 'rgba(255, 205, 215, 0.7)', stopOpacity: 1}} />
              <stop offset="100%" style={{stopColor: 'rgba(255, 182, 193, 0.8)', stopOpacity: 1}} />
            </linearGradient>
          </defs>
          <path d="M0,100 Q150,50 300,100 T600,100 L600,200 L0,200 Z" fill="url(#memoriesWaveGradient)"/>
        </svg>
      </div>

      {/* 底部区域 */}
      <div className="memories-bottom-section">
        <div className="memories-container">

          {/* 添加回忆表单 */}
          {showAddForm && (
            <motion.div 
              className="memory-form-container"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="form-title">
                <FaHeart className="form-icon" />
                记录美好回忆
              </h3>
              <form onSubmit={handleSubmit} className="memory-form">
                <div className="form-row-inline">
                  <div className="form-row">
                    <div className="input-group">
                      <FaHeart className="input-icon" />
                      <input
                        type="text"
                        name="title"
                        placeholder="回忆标题"
                        value={newMemory.title}
                        onChange={handleInputChange}
                        className="form-input"
                        maxLength="50"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="input-group">
                      <FaCalendarAlt className="input-icon" />
                      <input
                        type="date"
                        name="date"
                        value={newMemory.date}
                        onChange={handleInputChange}
                        className="form-input"
                      />
                    </div>
                  </div>
                </div>
                <div className="form-row-inline">
                  <div className="form-row">
                    <div className="input-group">
                      <FaMapMarkerAlt className="input-icon" />
                      <input
                        type="text"
                        name="location"
                        placeholder="地点"
                        value={newMemory.location}
                        onChange={handleInputChange}
                        className="form-input"
                        maxLength="30"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="input-group">
                      <span className="input-icon">😊</span>
                      <input
                        type="text"
                        name="mood"
                        placeholder="心情 (emoji)"
                        value={newMemory.mood}
                        onChange={handleInputChange}
                        className="form-input"
                        maxLength="5"
                      />
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="input-group">
                    <FaQuoteLeft className="input-icon textarea-icon" />
                    <textarea
                      name="description"
                      placeholder="详细描述这个美好的回忆..."
                      value={newMemory.description}
                      onChange={handleInputChange}
                      className="form-textarea"
                      maxLength="500"
                      rows="4"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="input-group">
                    <FaTag className="input-icon" />
                    <input
                      type="text"
                      name="tags"
                      placeholder="标签 (用逗号分隔)"
                      value={newMemory.tags}
                      onChange={handleInputChange}
                      className="form-input form-tags-input"
                      maxLength="100"
                    />
                  </div>
                </div>
                <div className="form-buttons">
                  <button 
                    type="submit" 
                    className={`submit-btn ${isSubmitting ? 'submitting' : ''}`}
                    disabled={isSubmitting}
                  >
                    <FaPaperPlane className="submit-icon" />
                    {isSubmitting ? '保存中...' : '保存回忆'}
                  </button>
                  <button 
                    type="button" 
                    className="cancel-btn"
                    onClick={cancelForm}
                    disabled={isSubmitting}
                  >
                    <FaTimes />
                    取消
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* 回忆列表 */}
          <motion.div
            className="memories-timeline"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-text">正在加载回忆...</p>
              </div>
            ) : memories.length === 0 ? (
              <div className="empty-container">
                <p className="empty-text">还没有回忆，快来记录第一个美好时光吧！</p>
              </div>
            ) : (
              // 按时间倒序排列，最新的在上面
              memories
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map((memory, index) => (
                <motion.div
                  key={memory.id}
                  className="memory-item"
                  initial={{ x: index % 2 === 0 ? -50 : 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.8 + index * 0.1, duration: 0.6 }}
                  onClick={() => openMemoryDetail(memory)}
                >
                  <div className="memory-date">
                    <FaCalendarAlt />
                    <span>{memory.date}</span>
                  </div>
                  <div className="memory-card">
                    <div className="memory-mood">{memory.mood}</div>
                    <h3 className="memory-title">{memory.title}</h3>
                    <div className="memory-location">
                      <FaMapMarkerAlt />
                      <span>{memory.location}</span>
                    </div>
                    <p className="memory-preview">
                      {memory.description.substring(0, 50)}...
                    </p>
                    <div className="memory-tags">
                      {memory.tags && memory.tags.map((tag, tagIndex) => (
                        <span key={tagIndex} className="memory-tag">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </motion.div>
        </div>
      </div>

      {/* 悬浮添加按钮 */}
      <motion.div
        className="add-memory-btn"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
      >
        <button 
          className="floating-btn"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? <FaTimes /> : <FaPlus />}
          <span>{showAddForm ? '取消' : '添加'}</span>
        </button>
      </motion.div>

      {/* 详情模态框 */}
      {selectedMemory && (
        <motion.div
          className="memory-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeMemoryDetail}
        >
          <motion.div
            className="memory-detail"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="detail-header">
              <div className="detail-mood">{selectedMemory.mood}</div>
              <button className="close-btn" onClick={closeMemoryDetail}>
                ×
              </button>
            </div>
            <h2 className="detail-title">{selectedMemory.title}</h2>
            <div className="detail-meta">
              <div className="detail-date">
                <FaCalendarAlt />
                <span>{selectedMemory.date}</span>
              </div>
              <div className="detail-location">
                <FaMapMarkerAlt />
                <span>{selectedMemory.location}</span>
              </div>
            </div>
            <p className="detail-description">{selectedMemory.description}</p>
            <div className="detail-tags">
              {selectedMemory.tags.map((tag, tagIndex) => (
                <span key={tagIndex} className="detail-tag">
                  #{tag}
                </span>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default MemoriesPage;


